import { useEffect, useMemo } from "react";
import { useLocation } from "react-router";
import { styled } from "styled-components";

import { CarouselSlider, ListItemBox, Tile } from "common/components";
import { mediaStyles } from "common/styles";
import { useDispatch, useSelector } from "common/utilities/react-redux";
import { useTranslate } from "common/utilities/useTranslate";
import { applyInitiativesSearchCriteria } from "domains/initiatives/actions";
import {
  createInitiativesForTopicSelector,
  getInitiativesFilterCount,
  getIsInitiativesFiltered,
} from "domains/initiatives/selectors";
import { TopicDetailSection } from "domains/topicDetail/components/TopicDetailSection";
import { getTmSelectedId } from "domains/transformationMap/selectors";
import { useMedia } from "domains/viewport/useMedia";
import { createInitiativesSelector } from "entities/initiatives/selectors";

const StyledCarousel = styled(CarouselSlider)`
  padding: 0;
`;

const StyledCard = styled(ListItemBox)`
  padding: 1rem;
  ${mediaStyles(({ spacing }) => `margin-right: ${spacing}rem;`)}
`;

type InitiativesSectionProps = { selectedSectionId?: string; topicId: string };

export const InitiativesSection = ({ topicId, selectedSectionId }: InitiativesSectionProps) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const numberOfTiles = useMedia({ small: 1 }, 2);
  const filterCount = useSelector(getInitiativesFilterCount);
  const isFiltered = useSelector(getIsInitiativesFiltered);
  const selectedInnerNodeId = useSelector(getTmSelectedId);
  const translate = useTranslate();

  const id = selectedInnerNodeId ?? topicId;

  const initiativesForTopic = useSelector(createInitiativesForTopicSelector(id));

  const initiativesSelector = useMemo(
    () => createInitiativesSelector(initiativesForTopic?.data ?? []),
    [initiativesForTopic?.data],
  );

  const initiatives = useSelector(initiativesSelector);

  useEffect(() => {
    if (id && initiatives.length === 0) {
      dispatch(applyInitiativesSearchCriteria({ id, limit: 20, offset: 0 }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, id]);

  const isPending = initiativesForTopic?.isFetching ?? false;
  const path = `${location.pathname}/initiatives`;

  if (initiatives.length === 0 && !isPending && !isFiltered) {
    return null;
  }

  return (
    <TopicDetailSection
      display={initiatives.length !== 0 || !isPending}
      filterCount={filterCount}
      isHidden={!!selectedSectionId}
      isHorizontal
      seeAllLink={path}
      title={translate("app.discover.topic.tabs.initiatives.title")}
      topicId={topicId}
    >
      <StyledCarousel numberOfItems={numberOfTiles}>
        {initiatives.slice(0, 5).map((initiative) => (
          <Tile
            image={initiative.images?.thumbnail}
            key={initiative.id}
            link={`${path}/${initiative.id}`}
            title={initiative.name}
          />
        ))}
      </StyledCarousel>
      {initiatives.length === 0 && !isPending && isFiltered && (
        <StyledCard>{translate("app.monitor.empty_message_filter")}</StyledCard>
      )}
    </TopicDetailSection>
  );
};
